<template>
  <div class="app-input" :class="rootClasses">
    <label
      v-if="label"
      :for="inputId"
      class="app-input__label"
    >
      {{ label }}
      <span v-if="showRequiredMark" class="app-input__required" aria-hidden="true">*</span>
    </label>

    <div class="app-input__field">
      <span v-if="iconLeft" class="app-input__icon app-input__icon--left">
        <i :class="iconLeft"></i>
      </span>

      <slot name="prefix" />

      <input
        :id="inputId"
        ref="input"
        v-bind="$attrs"
        :type="resolvedType"
        :value="value"
        :placeholder="placeholder"
        :disabled="disabled"
        :readonly="readonly"
        :required="required"
        :aria-invalid="!!error || null"
        :aria-describedby="describedBy"
        class="app-input__control"
        @input="onInput"
        @blur="onBlur"
        @change="onChange"
        @focus="onFocus"
        @keydown="(e) => $emit('keydown', e)"
      />

      <button
        v-if="canShowClear"
        type="button"
        class="app-input__action"
        :aria-label="clearLabel"
        tabindex="-1"
        @click="onClear"
      >
        <i class="fa-regular fa-xmark"></i>
      </button>

      <button
        v-if="canTogglePassword"
        type="button"
        class="app-input__action"
        :aria-label="showPassword ? 'Hide password' : 'Show password'"
        tabindex="-1"
        @click="showPassword = !showPassword"
      >
        <i v-if="showPassword" class="fa-regular fa-eye-slash"></i>
        <i v-else class="fa-regular fa-eye"></i>
      </button>

      <span
        v-if="resolvedRightIcon && !canShowClear && !canTogglePassword"
        class="app-input__icon app-input__icon--right"
      >
        <i :class="resolvedRightIcon"></i>
      </span>

      <slot name="suffix" />
    </div>

    <small
      v-if="error"
      :id="errorId"
      class="app-input__error"
      v-html="error"
    ></small>
    <small
      v-else-if="help"
      :id="helpId"
      class="app-input__help"
      v-html="help"
    ></small>
  </div>
</template>

<script>
// Auto-incrementing ID for label↔input pairing when caller didn't pass `id`.
let uid = 0

/**
 * App-styled input — no-Bootstrap, drop-in upgrade over `c-input`.
 *
 * Migration notes from c-input:
 *   - Same v-model contract (value + input event).
 *   - Same prop names where it matters: label, error, help, icon, size,
 *     requiredMark, passwordToggle.
 *   - Extra knobs added: iconLeft, iconRight, clearable, variant, disabled.
 *
 * Slots:
 *   - prefix: custom content inside the field before the input (e.g. currency).
 *   - suffix: custom content after the input (e.g. unit label).
 *
 * Public methods (via $refs):
 *   - focus()
 *   - select()
 */
export default {
  name: 'AppInput',

  inheritAttrs: false,

  props: {
    value: {
      type: [String, Number, null],
      default: '',
    },
    type: {
      type: String,
      default: 'text',
    },
    label: {
      type: String,
      default: '',
    },
    placeholder: {
      type: String,
      default: '',
    },
    error: {
      type: String,
      default: '',
    },
    help: {
      type: String,
      default: '',
    },
    // c-input compat: trailing icon FA class (e.g. "fa-magnifying-glass").
    icon: {
      type: String,
      default: '',
    },
    iconRight: {
      type: String,
      default: '',
    },
    iconLeft: {
      type: String,
      default: '',
    },
    size: {
      type: String,
      default: 'md',
      validator: (v) => ['sm', 'md', 'lg'].includes(v),
    },
    variant: {
      type: String,
      default: 'default',
      validator: (v) => ['default', 'ghost'].includes(v),
    },
    requiredMark: {
      type: Boolean,
      default: false,
    },
    required: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    passwordToggle: {
      type: Boolean,
      default: false,
    },
    clearable: {
      type: Boolean,
      default: false,
    },
    clearLabel: {
      type: String,
      default: 'Clear',
    },
    id: {
      type: String,
      default: '',
    },
  },

  emits: ['input', 'blur', 'change', 'focus', 'clear', 'keydown'],

  data() {
    return {
      showPassword: false,
      autoId: `app-input-${++uid}`,
    }
  },

  computed: {
    inputId()  { return this.id || this.autoId },
    errorId()  { return `${this.inputId}-error` },
    helpId()   { return `${this.inputId}-help` },

    describedBy() {
      if (this.error) return this.errorId
      if (this.help) return this.helpId
      return null
    },

    isPassword() { return this.type === 'password' },

    canTogglePassword() {
      return this.isPassword && this.passwordToggle
    },

    resolvedType() {
      if (this.canTogglePassword) return this.showPassword ? 'text' : 'password'
      return this.type
    },

    // Explicit `iconRight` wins over c-input-style `icon`.
    resolvedRightIcon() {
      return this.iconRight || this.icon
    },

    hasRightSlotContent() {
      return !!this.resolvedRightIcon || this.canTogglePassword || this.canShowClear
    },

    canShowClear() {
      return this.clearable
        && !this.disabled
        && !this.readonly
        && String(this.value ?? '').length > 0
    },

    showRequiredMark() {
      return this.requiredMark || this.required
    },

    rootClasses() {
      return [
        `app-input--${this.size}`,
        `app-input--${this.variant}`,
        {
          'app-input--has-left-icon':  !!this.iconLeft,
          'app-input--has-right-icon': this.hasRightSlotContent,
          'app-input--has-error':      !!this.error,
          'app-input--disabled':       this.disabled,
        },
      ]
    },
  },

  methods: {
    onInput(e)  { this.$emit('input', e.target.value) },
    onBlur(e)   { this.$emit('blur', e.target.value) },
    onChange(e) { this.$emit('change', e.target.value) },
    onFocus(e)  { this.$emit('focus', e) },

    onClear() {
      this.$emit('input', '')
      this.$emit('clear')
      // Restore focus so keyboard users don't lose context.
      this.$nextTick(() => this.focus())
    },

    /** Public — programmatically focus the inner input. */
    focus() {
      if (this.$refs.input) this.$refs.input.focus()
    },

    /** Public — programmatically select the input's contents. */
    select() {
      if (this.$refs.input) this.$refs.input.select()
    },
  },
}
</script>

<style lang="scss" scoped>
// Local design tokens. Swap to CSS custom properties later if a theming
// layer is introduced — naming `$ai-*` is short enough for readability.
$ai-text:         #2b3a4a;
$ai-text-muted:   #6c7a89;
$ai-text-subtle:  #9aa6b3;
$ai-bg-surface:   #ffffff;
$ai-bg-soft:      #f6f8fa;
$ai-border:       rgba(0, 0, 0, 0.10);
$ai-blue-focus:   #4392d4;
$ai-blue-ring:    rgba(67, 146, 212, 0.16);
$ai-red:          #d04848;
$ai-red-bg:       rgba(208, 72, 72, 0.08);
$ai-red-ring:     rgba(208, 72, 72, 0.18);
$ai-radius-sm:    6px;
$ai-radius-md:    8px;
$ai-radius-lg:    10px;
$ai-duration:     140ms;
$ai-ease:         cubic-bezier(.4, 0, .2, 1);

.app-input {
  display: flex;
  flex-direction: column;
  min-width: 0;

  // ── Label ──
  &__label {
    font-size: 11px;
    font-weight: 700;
    color: $ai-text-muted;
    text-transform: uppercase;
    letter-spacing: .04em;
    margin-bottom: 6px;
    line-height: 1.2;
  }

  &__required {
    color: $ai-red;
    margin-left: 2px;
  }

  // ── Field wrapper (relative positioning anchor) ──
  &__field {
    position: relative;
    display: flex;
    align-items: center;
    min-width: 0;
  }

  // ── Control (the actual <input>) ──
  &__control {
    flex: 1;
    width: 100%;
    height: 36px;
    padding: 0 12px;
    font-size: 13px;
    font-family: inherit;
    color: $ai-text;
    background: $ai-bg-soft;
    border: 0.5px solid $ai-border;
    border-radius: $ai-radius-md;
    transition:
      background  $ai-duration $ai-ease,
      border-color $ai-duration $ai-ease,
      box-shadow  $ai-duration $ai-ease,
      color       $ai-duration $ai-ease;

    &::placeholder { color: $ai-text-subtle; }
    &::-ms-clear   { display: none; } // hide IE/Edge native clear — we own it

    &:hover:not(:disabled):not(:read-only) {
      background: lighten($ai-bg-soft, 1%);
    }

    &:focus {
      outline: none;
      background: $ai-bg-surface;
      border-color: $ai-blue-focus;
      box-shadow: 0 0 0 3px $ai-blue-ring;
    }

    &:disabled,
    &[disabled] {
      color: $ai-text-subtle;
      background: $ai-bg-soft;
      cursor: not-allowed;
      opacity: 0.7;
    }

    &:read-only {
      cursor: default;
    }

    // Native date/time inputs add internal padding to ::-webkit-datetime-edit
    // which can break our height target. Zero it out and let the control's
    // own height + line-height define the visible size, so a `type="date"`
    // sits at the exact same height as `type="text"` or `<app-select>`.
    &[type='date'],
    &[type='datetime-local'],
    &[type='time'],
    &[type='month'],
    &[type='week'] {
      // line-height drives vertical centering of the native edit text.
      line-height: 1.2;

      &::-webkit-datetime-edit {
        padding: 0;
        line-height: 1.2;
      }
      &::-webkit-calendar-picker-indicator {
        // Make the picker icon clickable across the input height without
        // forcing extra padding around it.
        padding: 0 2px;
        cursor: pointer;
        opacity: 0.6;
        transition: opacity $ai-duration $ai-ease;

        &:hover { opacity: 1; }
      }
    }
  }

  // ── Decorative icons (non-interactive) ──
  &__icon {
    position: absolute;
    top: 0;
    bottom: 0;
    display: inline-flex;
    align-items: center;
    color: $ai-text-subtle;
    font-size: 12px;
    pointer-events: none;

    &--left  { left: 12px; }
    &--right { right: 12px; }
  }

  // ── Interactive in-field buttons (clear, password toggle) ──
  &__action {
    position: absolute;
    right: 8px;
    top: 50%;
    transform: translateY(-50%);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 22px;
    height: 22px;
    border: none;
    background: transparent;
    color: $ai-text-subtle;
    border-radius: $ai-radius-sm;
    cursor: pointer;
    transition: background $ai-duration $ai-ease, color $ai-duration $ai-ease;
    font-family: inherit;
    font-size: 12px;

    &:hover {
      background: rgba(0, 0, 0, 0.04);
      color: $ai-text;
    }

    &:focus-visible {
      outline: 2px solid $ai-blue-focus;
      outline-offset: 1px;
    }
  }

  // Stack multiple right-side actions side-by-side.
  &__action + &__action {
    right: 34px;
  }

  // ── Padding compensation when icons/buttons occupy the corners ──
  &--has-left-icon  &__control { padding-left:  34px; }
  &--has-right-icon &__control { padding-right: 34px; }

  // ── Sizes ──
  &--sm &__control {
    height: 28px;
    padding: 0 10px;
    font-size: 12px;
  }
  &--sm.app-input--has-left-icon  &__control { padding-left:  28px; }
  &--sm.app-input--has-right-icon &__control { padding-right: 28px; }
  &--sm &__icon--left  { left: 9px; }
  &--sm &__icon--right { right: 9px; }
  &--sm &__action      { width: 18px; height: 18px; right: 6px; }
  &--sm &__action + &__action { right: 28px; }

  &--lg &__control {
    height: 44px;
    padding: 0 14px;
    font-size: 14px;
    border-radius: $ai-radius-lg;
  }
  &--lg.app-input--has-left-icon  &__control { padding-left:  40px; }
  &--lg.app-input--has-right-icon &__control { padding-right: 40px; }
  &--lg &__icon--left  { left: 14px; }
  &--lg &__icon--right { right: 14px; }
  &--lg &__action      { width: 26px; height: 26px; }
  &--lg &__action + &__action { right: 40px; }

  // ── Variants ──
  &--ghost &__control {
    background: transparent;
    border-color: transparent;

    &:hover:not(:disabled):not(:read-only) {
      background: $ai-bg-soft;
    }
    &:focus {
      background: $ai-bg-surface;
      border-color: $ai-blue-focus;
    }
  }

  // ── Error state ──
  &--has-error &__control {
    border-color: $ai-red;
    background: $ai-red-bg;

    &:focus { box-shadow: 0 0 0 3px $ai-red-ring; }
  }

  &--has-error &__icon { color: $ai-red; }

  // ── Disabled state (root-level kill switch) ──
  &--disabled {
    pointer-events: none;
    opacity: 0.7;
  }

  // ── Inline messages ──
  &__error,
  &__help {
    font-size: 11px;
    margin-top: 4px;
    line-height: 1.4;
  }

  &__error { color: $ai-red; }
  &__help  { color: $ai-text-muted; }
}
</style>
